import { Loader2 } from "lucide-react";

export type LoadingTone = "primary" | "muted" | "inverse";

const TONE_CLASS_NAMES: Record<LoadingTone, { text: string; glow: string }> = {
  primary: { text: "text-primary", glow: "bg-primary" },
  muted: { text: "text-muted-foreground", glow: "bg-muted-foreground" },
  inverse: { text: "text-text-on-brand", glow: "bg-text-on-brand" },
};

interface LoadingProps {
  size?: number;
  /** Spinner colour. `inverse` is for spinners sitting on a filled brand surface. */
  tone?: LoadingTone;
  fullScreen?: boolean;
  overlay?: boolean;
  text?: string;
}

export const Loading = ({
  size = 48,
  tone = "primary",
  fullScreen = true,
  overlay = false,
  text,
}: LoadingProps) => {
  const { text: textClassName, glow: glowClassName } = TONE_CLASS_NAMES[tone];

  const spinner = (
    <div className="flex flex-col items-center gap-4">
      <Loader2
        className={`animate-spin ${textClassName}`}
        style={{ width: size, height: size }}
        aria-label="Loading"
      />
      {text && <p className={`text-sm font-medium ${textClassName}`}>{text}</p>}
    </div>
  );

  if (overlay) {
    return (
      <div className="fixed inset-0 z-overlay flex items-center justify-center bg-foreground/10 backdrop-blur-[6px] transition-all duration-300 animate-in fade-in">
        <div className="relative flex flex-col items-center gap-7 rounded-2xl border border-white/60 bg-card/80 p-14 shadow-[0_32px_64px_-16px_rgba(0,0,0,0.12)] backdrop-blur-3xl duration-300 animate-in zoom-in-95">
          <div className="relative">
            {/* Soft Ambient Glow */}
            <div
              className={`absolute inset-0 animate-pulse rounded-full opacity-30 blur-3xl transition-all duration-300 ${glowClassName}`}
            />
            <Loader2
              className={`relative animate-spin ${textClassName} opacity-90`}
              style={{ width: size, height: size, strokeWidth: 1.2 }}
            />
          </div>

          {text && (
            <div className="flex flex-col items-center gap-2 text-center">
              <h3 className="px-6 text-lg font-bold leading-tight tracking-tight text-foreground">
                {text}
              </h3>
              <div className="flex items-center gap-2">
                <span className="h-[1px] w-4 bg-border" />
                <p className="text-2xs font-semibold uppercase tracking-[0.1em] text-text-disabled">
                  Please wait a moment
                </p>
                <span className="h-[1px] w-4 bg-border" />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (fullScreen) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-sunken p-4">
        {spinner}
      </div>
    );
  }
  return spinner;
};
